import { dequal } from 'dequal'

import type { Config as ReminderConfig } from 'Automations/nodeDefinitions/definitions/reminder'
import {
  AutomationsPresentationV1Edge,
  AutomationsPresentationV1Node
} from '../../types'
import {
  findClosestUpstreamReminderRecipients,
  REMINDER_NODE_TYPE,
  REMINDER_RECIPIENT_FIELDS,
  ReminderRecipients
} from './findClosestUpstreamReminderRecipients'

/**
 * Return `nodes` with the reminder `nodeId`'s recipient addresses (to/cc/bcc)
 * replaced by those of the closest upstream reminder (BADGE-5974). Only the
 * addressing is copied — the subject, body, schedule and approval are left
 * as-is.
 *
 * The original `nodes` array reference is returned unchanged when there is
 * nothing to do, so callers can skip the state update when it is:
 *   - not a reminder,
 *   - a reminder whose recipients have already been edited away from
 *     `defaultRecipients` (a user's addresses are never clobbered — but a node
 *     whose recipients are still default is filled even if its message was
 *     edited),
 *   - a reminder with no upstream reminder to copy from, or
 *   - a reminder that already matches its upstream (e.g. both still default).
 */
export const prefillReminderRecipientsFromUpstream = ({
  nodeId,
  nodes,
  edges,
  defaultRecipients
}: {
  nodeId: string
  nodes: AutomationsPresentationV1Node[]
  edges: AutomationsPresentationV1Edge[]
  defaultRecipients: ReminderRecipients
}): AutomationsPresentationV1Node[] => {
  const targetNode = nodes.find(node => node.id === nodeId)
  if (!targetNode || targetNode.type !== REMINDER_NODE_TYPE) return nodes

  const targetConfig = targetNode.data as ReminderConfig
  const recipientsAreUntouched = REMINDER_RECIPIENT_FIELDS.every(field =>
    dequal(targetConfig[field], defaultRecipients[field])
  )
  if (!recipientsAreUntouched) return nodes

  const upstreamRecipients = findClosestUpstreamReminderRecipients({
    nodeId,
    nodes,
    edges
  })
  if (!upstreamRecipients) return nodes

  // Nothing to do if the target already matches the upstream (e.g. both still
  // default) — return the same reference so callers can skip the update.
  const alreadyMatchesUpstream = REMINDER_RECIPIENT_FIELDS.every(field =>
    dequal(targetConfig[field], upstreamRecipients[field])
  )
  if (alreadyMatchesUpstream) return nodes

  return nodes.map(node =>
    node.id === nodeId
      ? { ...node, data: { ...node.data, ...upstreamRecipients } }
      : node
  )
}
